import Image from "components/base/Image";
import convertUnits from "utils/converter";

export default [
  {
    field: "images",
    displayName: "Images",
    value: (product) => {
      if (!product.images) return product.id;
      return (
        <div className="flex items-center justify-center relative">
          {product.images.map((image, index) => (
            <a
              key={product.id + "-" + index}
              className="block mx-3"
              href={image.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image size="w-12 h-12 z-0" src={image.url} />
            </a>
          ))}
        </div>
      );
    },
  },

  // New Ordering, 2025-07-15
  { field: "number", displayName: "Item Number" },
  { field: "version", displayName: "Version" },
  { field: "cccInfo", displayName: "CCC Info" },
  {
    field: "barcodes",
    displayName: "Barcodes",
    value: (product) => {
      let res = "";
      const barcodeFields = [
        {
          label: "Inner Box(EAN)",
          value: product.innerBoxBarcodeEan,
        },
        {
          label: "Inner Box(UPC)",
          value: product.innerBoxBarcodeUpc,
        },
        {
          label: "Inner Carton(EAN/GTIN14)",
          value: product.innerCartonBarcodeEan,
        },
        {
          label: "Inner Carton(UPC/GTIN14)",
          value: product.innerCartonBarcodeUpc,
        },
        {
          label: "Outer Carton(EAN/GTIN14)",
          value: product.outerCartonBarcodeEan,
        },
        {
          label: "Outer Carton(UPC/GTIN14)",
          value: product.outerCartonBarcodeUpc,
        },
        {
          label: "Inner Box(JD)",
          value: product.innerBoxBarcodeJd,
        },
        {
          label: "Outer Carton(JD)",
          value: product.outerCartonBarcodeJd,
        },
      ];

      barcodeFields.forEach((field) => {
        if (field.value) {
          res += `${field.label}: ${field.value}\n`;
        }
      });
      return res;
    },
  },
  { field: "quantityPerCarton", displayName: "Qty/Carton" },
  {
    field: "outerCartonSize",
    displayName: "Outer Carton Size (±5%)",
    value: (product, metric) =>
      convertUnits(
        [product.outerCartonX, product.outerCartonZ, product.outerCartonY],
        "cm",
        "in",
        metric,
      ),
  },
  {
    field: "ctnNetWeight",
    displayName: "CTN Net Weight (±5%)",
    value: (product, metric) =>
      convertUnits([product.ctnNetWeight], "kg", "lb", metric),
  },
  {
    field: "ctnGrossWeight",
    displayName: "CTN Gross Weight (±5%)",
    value: (product, metric) =>
      convertUnits([product.ctnGrossWeight], "kg", "lb", metric),
  },
  {
    field: "outerCartonCbm",
    displayName: "Outer Carton CBM (±5%)",
    value: (product, metric) =>
      convertUnits([product.outerCartonCbm], "m3", "ft3", metric),
  },

  { field: "quantityPerDisplayBox", displayName: "Qty/Display Box" },
  {
    field: "itemWeight",
    displayName: "Item Weight (±5%)",
    value: (product, metric) => {
      return convertUnits([product.itemWeight], "g", "lb", metric);
    },
  },
  {
    field: "grossWeight",
    displayName: "Gross Weight (±5%)",
    value: (product, metric) =>
      convertUnits([product.grossWeight], "g", "lb", metric),
  },
  {
    field: "itemSize",
    displayName: "Item Size (±5%)",
    value: (product, metric) => {
      if (product.shape === "ball") {
        return convertUnits([product.ballDiameter], "mm", "in", metric);
      } else if (product.shape === "cube") {
        return convertUnits(
          [product.cubeX, product.cubeZ, product.cubeY],
          "mm",
          "in",
          metric,
        );
      } else if (product.shape === "flat") {
        return convertUnits([product.flatX, product.flatY], "mm", "in", metric);
      } else if (product.shape === "multi") {
        return product.multiSizes;
      }
      return convertUnits(
        [product.cubeX, product.cubeZ, product.cubeY],
        "mm",
        "in",
        metric,
      );
    },
  },
  {
    field: "hsCode",
    displayName: "HS Code",
    value: (product) => {
      if (!product.productLine) return "";
      return `${product.productLine.hsCode} ${product.productLine.hsCodeForEu ? `EU: ${product.productLine.hsCodeForEu}` : ""} ${
        product.productLine.hsCodeForUs
          ? `US: ${product.productLine.hsCodeForUs}`
          : ""
      }`;
    },
  },
  {
    field: "productMaterial",
    displayName: "Product Material",
    value: (product) => {
      // If product has children and at least one of them has materials, show them
      if (product && product.children && product.children.length > 0) {
        const materialsList = product.children
          .filter((p_m) => p_m.product.materials.length > 0)
          .map(
            (p_m) =>
              `- ${p_m.product.name}: ${p_m.product.materials.map((m) => m.material.name).join(", ")}`,
          )
          .join("\n");

        if (materialsList) {
          return materialsList;
        }
      }

      // otherwise show product materials
      if (product && product.materials?.length > 0) {
        return product.materials.map((i) => i.material.name).join(", ");
      }

      // fallback to product line materials
      if (product?.productLine?.materials?.length > 0) {
        return product.productLine.materials
          .map((i) => i.material.name)
          .join(", ");
      }

      return " - ";
    },
  },
  { field: "pantone", displayName: "Pantone" },
  {
    field: "age",
    displayName: "Age Grading",
    value: (product) => {
      return product.productLine?.age || "";
    },
  },
  {
    field: "warnings",
    displayName: "Warnings",
    value: (product) => {
      return product.warnings.map((i) => i.name).join(", ");
    },
  },
  {
    field: "labTestStandards",
    displayName: "Test Reports",
    value: (product) => {
      return product.labTestStandards.map((i) => i.name).join(", ");
    },
  },
  { field: "moq", displayName: "MOQ" },
  { field: "packing", displayName: "Packing" },
  {
    field: "innerBoxSize",
    displayName: "Inner Box/Clam Shell Size (±5%)",
    value: (product, metric) =>
      convertUnits(
        [product.innerBoxX, product.innerBoxZ, product.innerBoxY],
        "mm",
        "in",
        metric,
      ),
  },

  {
    field: "displayBoxSize",
    displayName: "Display Box Size (±5%)",
    value: (product, metric) =>
      convertUnits(
        [product.displayBoxX, product.displayBoxZ, product.displayBoxY],
        "mm",
        "in",
        metric,
      ),
  },
  {
    field: "innerCartonSize",
    displayName: "Inner Carton Size (±5%)",
    value: (product, metric) =>
      convertUnits(
        [product.innerCartonX, product.innerCartonZ, product.innerCartonY],
        "mm",
        "in",
        metric,
      ),
  },
];
